import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, Check, AlertCircle } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import SEOHead from '../components/shared/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', org: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [responseMsg, setResponseMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organisation: form.org || null,
          message: form.message,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus('success');
        setResponseMsg(data.message);
        setForm({ name: '', email: '', org: '', message: '' });
      } else {
        setStatus('error');
        setResponseMsg('Something went wrong. Please try emailing ollie@kalobrate.com directly.');
      }
    } catch {
      setStatus('error');
      setResponseMsg('Could not send your message. Please try emailing ollie@kalobrate.com directly.');
    }
  };

  return (
    <PageLayout>
      <SEOHead
        title="Contact | Kalobrate"
        description="Get in touch with Kalobrate to discuss workplace culture, employee listening, leadership support or any workplace challenge you are facing."
        canonical="/contact"
      />

      <section className="bg-carbon py-20 md:py-28 relative overflow-hidden">
        <img src="/assets/kalobrate-icon-outline-yellow.png" alt="" aria-hidden="true" className="absolute top-[-10%] right-[-5%] w-[55%] h-[120%] object-contain opacity-[0.06] pointer-events-none select-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            Not sure what is driving the <span className="yellow-highlight text-carbon">problem</span>?
          </motion.h1>
          <motion.p className="text-base text-white/80 leading-relaxed" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            A first conversation can help clarify what you already know, what evidence may be missing and whether Kalobrate is the right support.
          </motion.p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <h2 className="text-xl font-heading font-bold text-carbon mb-6 tracking-tight">Send a message</h2>

              {status === 'success' ? (
                <div className="bg-green-50 border border-green-200 p-6" data-testid="contact-success">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-800 mb-1">Message sent</p>
                      <p className="text-sm text-green-700">{responseMsg}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setStatus(null)}
                    className="mt-4 text-sm text-green-700 underline hover:text-green-900"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="contact-error">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{responseMsg}</p>
                    </div>
                  )}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-carbon mb-1">Name</label>
                    <input id="name" type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-golden transition-colors" data-testid="contact-name" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-carbon mb-1">Email</label>
                    <input id="email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-golden transition-colors" data-testid="contact-email" />
                  </div>
                  <div>
                    <label htmlFor="org" className="block text-sm font-medium text-carbon mb-1">Organisation <span className="text-carbon/40">(optional)</span></label>
                    <input id="org" type="text" value={form.org} onChange={e => setForm({...form, org: e.target.value})} className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-golden transition-colors" data-testid="contact-org" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-carbon mb-1">What prompted your enquiry?</label>
                    <textarea id="message" rows={5} required value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-golden transition-colors resize-none" data-testid="contact-message" />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="bg-golden text-carbon px-7 py-3 font-semibold text-sm hover:bg-golden/90 transition-colors inline-flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {status === 'sending' ? 'Sending...' : 'Send message'}
                    {status !== 'sending' && <ArrowRight className="ml-2 w-4 h-4" />}
                  </button>
                  <p className="text-xs text-carbon/50 leading-relaxed mt-3">
                    By submitting this form, you agree that Kalobrate may store and use this information to respond to your enquiry. Your data is held securely and will not be shared with third parties. See our <a href="/privacy" className="underline hover:text-carbon transition-colors">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </div>

            <div className="lg:col-span-2">
              <h2 className="text-xl font-heading font-bold text-carbon mb-6 tracking-tight">Or get in touch directly</h2>
              <div className="space-y-6 text-sm">
                <div>
                  <p className="font-semibold text-carbon mb-1">Email</p>
                  <a href="mailto:ollie@kalobrate.com" className="text-carbon/70 hover:text-golden transition-colors flex items-center gap-2">
                    <Mail className="w-4 h-4" /> ollie@kalobrate.com
                  </a>
                </div>
                <div>
                  <p className="font-semibold text-carbon mb-1">LinkedIn</p>
                  <a href="https://www.linkedin.com/in/ollie-bell-phd/" target="_blank" rel="noopener noreferrer" className="text-carbon/70 hover:text-golden transition-colors">
                    linkedin.com/in/ollie-bell-phd
                  </a>
                </div>
                <div className="bg-gray-50 border border-gray-100 p-5">
                  <p className="font-semibold text-carbon mb-2">What the first conversation covers</p>
                  <ul className="space-y-2 text-carbon/70">
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-golden rounded-full mt-2 flex-shrink-0" />What is prompting the enquiry</li>
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-golden rounded-full mt-2 flex-shrink-0" />What the organisation looks like</li>
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-golden rounded-full mt-2 flex-shrink-0" />What leaders have already noticed</li>
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-golden rounded-full mt-2 flex-shrink-0" />Whether Kalobrate is a useful fit</li>
                  </ul>
                  <p className="text-carbon/50 text-xs mt-3">You do not need to arrive with a fully defined brief.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ContactPage;
